import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useJourneyUpdatesSse, JourneySseEvent } from "@/modules/leads/hooks/use-journey-updates-sse.hook";
import { FeatureFlagsKeys } from "@/modules/common/context/feature-flags.constants";
import {
  markNawyNowStreamClosed,
  markNawyNowStreamOpen,
} from "@/modules/nawy-now/hooks/realtime-status";

/** One invalidation per window, no matter how chatty the case gets (autosave bursts, OCR sweeps). */
const INVALIDATE_DEBOUNCE_MS = 1_500;

interface Params {
  orgUid?: string;
  journeyUid?: string;
}

/**
 * §Realtime: subscribes the lead page to workflow-manager case events for the journey being viewed and
 * refetches the whole nawy-now tree when one lands. Events are invalidation hints only — the REST API stays
 * the source of truth, so a missed event costs one poll interval of staleness, never wrong data. Rides the
 * same journey SSE channel as the on-call indicator; gated by its own `sse_nawy_now` flag and dormant when
 * REACT_APP_SSE_GATEWAY_URL is unset (both enforced by the shared driver).
 */
export function useNawyNowSse({ orgUid, journeyUid }: Params): void {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Whether THIS hook currently counts towards the module-level open-stream tally. */
  const countedRef = useRef(false);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (countedRef.current) {
        countedRef.current = false;
        markNawyNowStreamClosed();
      }
    },
    [],
  );

  /**
   * §Realtime-poll-gate: publish the stream's health, and catch up on reconnect.
   *
   * The queries below stop polling while this reads live, so the gap this has to cover is everything
   * that happened while it did NOT: the browser tab was hidden (the driver closes the stream on
   * `visibilitychange`), the gateway blipped, a ticket was refused. One invalidation on the
   * down→up edge collapses that whole gap into a single refetch — which is why the queries can then
   * drop `refetchOnWindowFocus` entirely instead of refetching on every alt-tab.
   */
  const onStatusChange = useCallback(
    (live: boolean) => {
      if (live === countedRef.current) return;
      countedRef.current = live;
      if (live) {
        markNawyNowStreamOpen();
        queryClient.invalidateQueries({ queryKey: ["nawy-now"] });
        return;
      }
      markNawyNowStreamClosed();
    },
    [queryClient],
  );

  const onSseEvent = useCallback(
    (event: JourneySseEvent) => {
      if (event.type !== "nawy_now") return;
      if (debounceRef.current) return; // an invalidation is already scheduled for this window
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        // The module's blessed coarse invalidation (same call every mutation hook makes) —
        // board, step details, participants, and case resolution refetch; option-sources
        // is deliberately outside this prefix.
        queryClient.invalidateQueries({ queryKey: ["nawy-now"] });
      }, INVALIDATE_DEBOUNCE_MS);
    },
    [queryClient],
  );

  useJourneyUpdatesSse({
    orgUid,
    visibleJourneyUids: journeyUid ? [journeyUid] : [],
    onEvent: onSseEvent,
    enabled: Boolean(orgUid && journeyUid),
    flagKey: FeatureFlagsKeys.SSE_NAWY_NOW,
    onStatusChange,
  });
}
